import type { AbstractAnalyticsDispatcher } from "@asurion-core/eventualize";
import type { CountryCode } from "libphonenumber-js";

import { InternationalMdnInput, removeMdnFormatting } from "@expert/common-ui";
import { Button, Flex, Input, Text } from "@mantine/core";
import { useState } from "react";
import { IMaskInput } from "react-imask";

import { withPolymorphicClickAnalytics } from "@/analytics";
import { isBlocklisted } from "@/customer-panel/components/customer-verification/utils/isBlocklisted";
import { formatPhoneNumber, parsePhoneNumber, validatePhoneNumber } from "@/shared-utils";

import classes from "./CustomerVerification.module.css";

interface SimplifiedMdnSectionProps {
    initialMdn: string;
    isEditing: boolean;
    onComplete: (input: string) => void;
    onEditClick: () => void;
    shouldAutoFocus?: boolean;
    /**
     * Required key prop to reset component state when needed.
     * Pass a unique value (like session ID) to reset state.
     */
    key: string | number;
    isInternationalPhoneEnabled: boolean;
    shouldSkipValidation: boolean | undefined;
    defaultCountry: CountryCode;
    dispatcher: AbstractAnalyticsDispatcher;
    blocklistedNumbers: string[] | undefined;
    blocklistedPrefixes: string[] | undefined;
}

function isPhoneNumberValidOrPossible(inputMdn: string, shouldSkipValidation: boolean) {
    return shouldSkipValidation ? parsePhoneNumber(inputMdn).possible : validatePhoneNumber(inputMdn).valid;
}

export function SimplifiedMdnSection({
    initialMdn,
    isEditing,
    onComplete,
    onEditClick,
    shouldAutoFocus = false,
    isInternationalPhoneEnabled,
    defaultCountry,
    dispatcher,
    blocklistedNumbers,
    blocklistedPrefixes,
    shouldSkipValidation,
}: SimplifiedMdnSectionProps) {
    const ButtonWithAnalytics = withPolymorphicClickAnalytics(Button<"button">, "Click");
    const [inputMdn, setInputMdn] = useState<string>(initialMdn);
    const [error, setError] = useState<string | null>(null);

    function onClickSave() {
        const strippedInputMdn = removeMdnFormatting(inputMdn);
        if (isBlocklisted(strippedInputMdn, blocklistedNumbers, blocklistedPrefixes)) {
            void dispatcher.dispatchBusinessEvent("BlocklistedMdnSubmitted");

            setError("This isn’t the customer’s phone number. Please ask for the right one.");
            return;
        }
        void dispatcher.dispatchBusinessEvent("MDN_Submitted");
        setError(null);
        onComplete(strippedInputMdn);
    }

    const onInputChange = ({ currentTarget: { value } }: React.InputEvent<HTMLInputElement>) => {
        setInputMdn(value);
    };
    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const isValidOrPossible = isPhoneNumberValidOrPossible(inputMdn, !!shouldSkipValidation);

        if (event.key === "Enter" && isValidOrPossible) {
            void dispatcher.dispatchUserAction("EnterKey", "MdnContinue");
            onClickSave();
        }
    };

    const phoneQuestionId = "phone-number-question";
    const props = {
        "aria-label": "Phone Number",
        "aria-labelledby": phoneQuestionId,
        "aria-invalid": !!error,
        id: "phone-input",
        autoFocus: shouldAutoFocus,
        size: "md" as const,
        rightSectionPointerEvents: "all" as const,
        rightSectionWidth: 100,
        onKeyDown,
        classNames: {
            wrapper: classes.styledInputWrapper,
            input: classes.styledInput,
        },
    };

    if (!isEditing && !inputMdn) return null;

    return (
        <Flex direction="column">
            <Text c="dark.1" size="sm" mb="md" mt="md" id={phoneQuestionId}>
                What’s the phone number you’re helping them with?
            </Text>
            {isEditing ? (
                <>
                    <Input.Wrapper
                        data-testid="mdn-edit"
                        error={error}
                        className="fs-mask"
                        classNames={{
                            label: classes.label,
                        }}
                    >
                        {isInternationalPhoneEnabled ? (
                            <InternationalMdnInput
                                onInput={onInputChange}
                                value={inputMdn}
                                {...props}
                                defaultCountry={defaultCountry}
                                data-testid="international-phone-input-mdn-section"
                            />
                        ) : (
                            <Input
                                value={inputMdn}
                                component={IMaskInput}
                                onInput={onInputChange}
                                mask="+1 (000) 000-0000"
                                onChange={(event) => {
                                    setInputMdn(event.currentTarget.value);
                                }}
                                data-testid="masked-input-mdn-section"
                                {...props}
                            />
                        )}
                    </Input.Wrapper>
                    <ButtonWithAnalytics
                        size="sm"
                        fz="xs"
                        mt="sm"
                        disabled={inputMdn ? !isPhoneNumberValidOrPossible(inputMdn, !!shouldSkipValidation) : true}
                        onClick={onClickSave}
                        analytics={{ actionId: "SavePhoneNumber" }}
                        aria-label="Save phone number"
                    >
                        Save
                    </ButtonWithAnalytics>
                </>
            ) : (
                <Flex align="center" justify="space-between" aria-labelledby={phoneQuestionId}>
                    <Text
                        size="sm"
                        c="dark.1"
                        className="fs-mask"
                        id="saved-phone-number"
                        role="status"
                        aria-live="polite"
                    >
                        {formatPhoneNumber(inputMdn, defaultCountry, shouldSkipValidation).number}
                    </Text>
                    <ButtonWithAnalytics
                        td="underline"
                        variant="subtle"
                        color="dark.1"
                        size="sm"
                        fz="xs"
                        onClick={onEditClick}
                        analytics={{ actionId: "change phone number" }}
                        aria-label="Change phone number"
                    >
                        Change
                    </ButtonWithAnalytics>
                </Flex>
            )}
        </Flex>
    );
}
